import React, { useState, useEffect, useRef } from 'react';
import { homeApi } from '../../api/api';
import { parseYoutubeId, youtubeThumb } from '../../utils/youtube';
import { AdminUrlField } from '../components/AdminUI';

const DEFAULT_LIVE = {
  isLive: true,
  url: 'https://www.youtube.com/@Radhekrishnabapuofficial/live',
  label: 'Live Katha',
};

const DEFAULT_WELCOME = {
  url: 'https://www.youtube.com/watch?v=EjqtlB1b02o',
  thumbnail: '',
};

const HomeAdmin = () => {
  const [live, setLive] = useState(DEFAULT_LIVE);
  const [welcome, setWelcome] = useState(DEFAULT_WELCOME);
  const [thumbTab, setThumbTab] = useState('url'); // 'url' | 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [savingLive, setSavingLive] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    setLoading(true);
    homeApi
      .getAll()
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          if (d.live_stream && typeof d.live_stream === 'object') {
            setLive({
              isLive: Boolean(d.live_stream.isLive),
              url: d.live_stream.url || DEFAULT_LIVE.url,
              label: d.live_stream.label || DEFAULT_LIVE.label,
            });
          }
          if (d.welcome_video && typeof d.welcome_video === 'object') {
            setWelcome({
              url: d.welcome_video.url || DEFAULT_WELCOME.url,
              thumbnail: d.welcome_video.thumbnail || '',
            });
          } else if (typeof d.welcome_video === 'string' && d.welcome_video) {
            setWelcome({ url: d.welcome_video, thumbnail: '' });
          }
        } else if (res.message) {
          setError(res.message);
        }
      })
      .catch(() => setError('Failed to load home settings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    };
  }, [filePreview]);

  const liveVideoId = parseYoutubeId(live.url);
  const welcomeVideoId = parseYoutubeId(welcome.url);

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (filePreview) URL.revokeObjectURL(filePreview);
    setSelectedFile(file);
    setFilePreview(URL.createObjectURL(file));
  };

  const clearThumbnail = () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setSelectedFile(null);
    setFilePreview('');
    setWelcome((prev) => ({ ...prev, thumbnail: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const saveLive = async () => {
    setSavingLive(true);
    setError('');
    try {
      await homeApi.updateKey('live_stream', {
        isLive: Boolean(live.isLive),
        url: (live.url || '').trim(),
        label: (live.label || 'Live Katha').trim() || 'Live Katha',
        videoId: liveVideoId,
      });
      showToast('Live stream saved');
    } catch {
      setError('Failed to save live stream');
    } finally {
      setSavingLive(false);
    }
  };

  const saveWelcome = async () => {
    setSavingWelcome(true);
    setError('');
    try {
      let thumbnail = (welcome.thumbnail || '').trim();

      if (thumbTab === 'file' && selectedFile) {
        const fd = new FormData();
        fd.append('image', selectedFile);
        const up = await homeApi.uploadImage(fd);
        if (!up.success || !up.data || !up.data.url) {
          throw new Error(up.message || 'Upload failed');
        }
        thumbnail = up.data.url;
        setWelcome((prev) => ({ ...prev, thumbnail }));
        setSelectedFile(null);
        if (filePreview) URL.revokeObjectURL(filePreview);
        setFilePreview('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }

      await homeApi.updateKey('welcome_video', {
        url: (welcome.url || '').trim(),
        videoId: welcomeVideoId,
        thumbnail,
      });
      showToast('Welcome video saved');
    } catch (err) {
      setError(err.message || 'Failed to save welcome video');
    } finally {
      setSavingWelcome(false);
    }
  };

  const thumbPreviewSrc = filePreview || (welcome.thumbnail || '').trim();

  const thumbTabBtn = (tab, icon, label) => (
    <button
      type="button"
      onClick={() => setThumbTab(tab)}
      className={`flex-1 py-2 text-center transition-colors ${
        thumbTab === tab ? 'admin-chip-active' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <i className={`fas ${icon} mr-1`}></i>{label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-orange-500 text-2xl"></i>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Home</h2>
        <p className="text-sm text-gray-500 mt-0.5">Live stream widget and welcome video</p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Live stream */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 flex flex-col">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <i className="fas fa-broadcast-tower text-red-500"></i> Live Stream
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Controls the floating live player on the website. Off = hidden.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={live.isLive}
              onClick={() => setLive((prev) => ({ ...prev, isLive: !prev.isLive }))}
              className={`relative flex-shrink-0 w-14 h-8 rounded-full transition-colors ${
                live.isLive ? 'bg-red-500' : 'bg-gray-300'
              }`}
              title={live.isLive ? 'Live on' : 'Live off'}
            >
              <span
                className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  live.isLive ? 'transform translate-x-6' : ''
                }`}
              />
            </button>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  live.isLive ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${live.isLive ? 'bg-red-500' : 'bg-gray-400'}`} />
                {live.isLive ? 'ON — visible on site' : 'OFF — hidden'}
              </span>
            </div>

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              YouTube live / video URL
            </label>
            <AdminUrlField
              value={live.url}
              onChange={(e) => setLive((prev) => ({ ...prev, url: e.target.value }))}
              onClear={() => setLive((prev) => ({ ...prev, url: '' }))}
              placeholder="enter live url"
            />

            {liveVideoId && (
              <div className="mt-4 mx-auto rounded-2xl overflow-hidden border border-gray-100 bg-black admin-home-preview">
                <img
                  src={youtubeThumb(liveVideoId)}
                  alt="Live preview"
                  className="w-full aspect-video object-cover"
                />
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={saveLive}
              disabled={savingLive}
              className="admin-home-save-btn disabled:opacity-60"
            >
              {savingLive ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Welcome video */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 flex flex-col">
          <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
            <i className="fab fa-youtube text-red-500"></i> Welcome Video
          </h3>
          <p className="text-sm text-gray-500 mb-5">
            YouTube video shown in the Welcome section on the home page.
          </p>

          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              YouTube URL
            </label>
            <AdminUrlField
              value={welcome.url}
              onChange={(e) => setWelcome((prev) => ({ ...prev, url: e.target.value }))}
              onClear={() => setWelcome((prev) => ({ ...prev, url: '' }))}
              placeholder="enter video url"
            />

            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Thumbnail
              </label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium mb-3">
                {thumbTabBtn('url', 'fa-link', 'URL')}
                {thumbTabBtn('file', 'fa-upload', 'Upload')}
              </div>

              {thumbTab === 'url' && (
                <AdminUrlField
                  value={welcome.thumbnail}
                  onChange={(e) => {
                    setSelectedFile(null);
                    if (filePreview) URL.revokeObjectURL(filePreview);
                    setFilePreview('');
                    setWelcome((prev) => ({ ...prev, thumbnail: e.target.value }));
                  }}
                  onClear={clearThumbnail}
                  placeholder="https://... or /path/to/image.jpg"
                />
              )}

              {thumbTab === 'file' && (
                <div>
                  <div
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-orange-400 transition-colors"
                  >
                    {selectedFile ? (
                      <p className="text-sm text-green-600 font-medium">
                        <i className="fas fa-check-circle mr-2"></i>{selectedFile.name}
                      </p>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                        <p className="text-sm text-gray-500">Click to select image</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP up to 10 MB</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              )}
            </div>

            {thumbPreviewSrc ? (
              <div className="mt-4 mx-auto rounded-2xl overflow-hidden border border-gray-100 bg-black admin-home-preview">
                <img
                  src={thumbPreviewSrc}
                  alt="Welcome video thumbnail"
                  className="w-full aspect-video object-cover"
                />
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={saveWelcome}
              disabled={savingWelcome}
              className="admin-home-save-btn disabled:opacity-60"
            >
              {savingWelcome ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeAdmin;
